// src/Components/Forms/Financement/Salam/Demande/Demande.tsx
import { useState, useEffect } from 'react';
import { Tabs, Space, Button, Form, Spin } from 'antd';
import type { TabsProps } from 'antd';
import { UserOutlined, ContactsOutlined } from '@ant-design/icons';
import axios from 'axios';
import { Typography } from '@mui/material';
import Tab1 from './Tab1';
import Tab2 from './Tab2';
import Tab3 from './Tab3';
import Utilisateur from '../../../../../Models/Utilisateur';
import { useAuthHeader, useAuthUser } from '../../../../../Services/Auth/sessionAuth';
import Data from '../../../../../Data/Data.json';
import SweetAlert from "sweetalert2";
import { Crypter } from '../../../../../Functions/Security/Crypter';
import { DemandeSalamDto } from '../../../../../Models/DemandeSalam';
import ReformatDate from '../../../../../Functions/Traitement/ReformatDate';
import HelpInfos from '../../../../HelpInfo';
import HelpData from "../../../../../Data/Help.json";
import Help from '../../../../../Types/Help';
import formatNumber from '../../../../../Functions/Traitement/FormatNumber';
import DataTypeTable from '../../../../../Types/DataTypeTable';

interface SelectData {
  value: string;
  label: string;
}

const DemandeSalam = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
  const authHeader = useAuthHeader();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);

  const [dataGarantie, setDataGarantie] = useState<SelectData[]>([]);
  const [garantiesTableau, setGarantiesTableau] = useState<DataTypeTable[]>([]);

  // États pour les frais
  const [fraisData, setFraisData] = useState<any[]>([]);
  const [fraisOptions, setFraisOptions] = useState<{ value: string; label: string }[]>([]);
  const [fraisSelectionnes, setFraisSelectionnes] = useState<string[]>([]);
  const [isGarantieOblig, setIsGarantieOblig] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [consultActv, setConsultActv] = useState(false);
  const [ajoutActv, setAjoutActv] = useState(false);
  const [modifActv, setModifActv] = useState(true);
  const [enregActv, setEnregActv] = useState(true);
  const [supActv, setSupActv] = useState(true);
  const [impActv, setImpActv] = useState(true);
  const [actuActv, setActuActv] = useState(true);
  const [annulActv, setAnnulActv] = useState(true);
  const [sbmit, setSbmit] = useState("");

  // Observateurs
  const CodeSalam = Form.useWatch('CodeSalam', form);
  const Quantite = Form.useWatch('Quantite', form) || 0;
  const PrixUnitaire = Form.useWatch('PrixUnitaire', form) || 0;
  const CodeFrais = Form.useWatch('CodeFrais', form) || [];

  // Montant demandé (calculé)
  const montantDemande = (Number(Quantite) * Number(PrixUnitaire)) || 0;

  // Mise à jour du champ MontantDemande
  useEffect(() => {
    form.setFieldValue('MontantDemande', montantDemande);
  }, [montantDemande, form]);

  // ✅ Fonction de chargement des frais (explicite, calquée sur le Qard)
  const loadFraisParams = (codeSalam: string) => {
    if (!codeSalam) {
      setFraisData([]);
      setFraisOptions([]);
      setFraisSelectionnes([]);
      form.setFieldValue('FraisService', 0);
      form.setFieldValue('CodeFrais', []);
      setIsGarantieOblig(false);
      return;
    }

    const config = {
      method: 'get',
      url: Data.ApiUrl.ReadParamsSalam,
      headers: {
        'CypherCodeSalam': Crypter.encrypt(codeSalam),
        'Authorization': authHeader(),
        'Content-Type': 'application/json'
      }
    };

    axios.request(config)
      .then(response => {
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (res.StatutCode === 200) {
          const params = res.Result.Table1?.[0];
          setIsGarantieOblig( params?.IsGarantieObligatoir === true || params?.IsGarantieObligatoir === "true");

          const table4 = res.Result.Table4 || [];
          setFraisData(table4);

          const options = table4.map((item: any) => ({
            value: item.CODOPE,
            label: `${item.CODOPE} - ${item.LIBELL || ''}`
          }));
          setFraisOptions(options);

          let savedFrais = form.getFieldValue('CodeFrais') || [];
          // En mode ajout ou si vide, cocher tous les frais par défaut
          if (savedFrais.length === 0 && table4.length > 0) {
            savedFrais = table4.map((item: any) => item.CODOPE);
            form.setFieldValue('CodeFrais', savedFrais);
          }
          setFraisSelectionnes(savedFrais);
        }
      })
      .catch(error => {
        console.error("Erreur chargement paramètres Salam:", error);
        setFraisData([]);
        setFraisOptions([]);
        setFraisSelectionnes([]);
        form.setFieldValue('FraisService', 0);
        form.setFieldValue('CodeFrais', []);
        setIsGarantieOblig(false);
      });
  };

  // ✅ Recalcul réactif des frais (sans appel API)
  useEffect(() => {
    let total = 0;
    fraisData.forEach((item: any) => {
      if (fraisSelectionnes.includes(item.CODOPE)) {
        total += Number(item.MONFIX || 0) + (Number(item.POURCE || 0) / 100) * montantDemande;
      }
    });
    form.setFieldValue('FraisService', Math.round(total * 100) / 100);
  }, [montantDemande, fraisData, fraisSelectionnes, form]);

  // ----- Gestion des garanties -----
  function LoadGarantie() {
    setDataGarantie([]);
    let dataGarantie: SelectData[] = [];
    try {
      let numcli = form.getFieldValue('NUMCLI');
      if (!numcli) {
        setDataGarantie([]);
        return;
      }
      numcli += Data.LocalVariable.separator + 'CL';
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.GetGarantie,
        headers: {
          'Content-Type': 'application/json',
          Authorization: authHeader(),
          CypherSearch: Crypter.encrypt(JSON.stringify(numcli)),
        },
      };
      axios(config)
        .then((response) => {
          if (response.data) {
            const res = JSON.parse(Crypter.decrypt(response.data)).Value;
            if (res.StatutCode === 200) {
              const datas = res.Result.Table;
              const filtered = datas.filter((item: any) => item.CdEtat === '02');
              if (filtered.length === 0) {
                localStorage.setItem('garanties_client', '');
                setDataGarantie([]);
              } else {
                filtered.forEach((data: any) => {
                  dataGarantie.push({
                    value: data.codgar + '|' + data.dispo,
                    label: data.codgar + ' -> ' + formatNumber(parseFloat(data.VALGARANTI)) + ' XAF( disponible : ' + formatNumber(parseFloat(data.dispo + '')) + ' XAF)',
                  });
                });
                setDataGarantie(dataGarantie);
                localStorage.setItem('garanties_client', Crypter.encrypt(JSON.stringify(filtered)));
              }
            }
          }
        })
        .catch((error) => {
          console.error(error);
          SweetAlert.fire({ title: 'Erreur', text: error.toString(), icon: 'error' });
        });
    } catch (error) {
      SweetAlert.fire({ title: "Client non sélectionné", text: "Veuillez sélectionner un client.", icon: "warning" });
      localStorage.setItem('garanties_client', '');
    }
  }

  // ----- Gestion des boutons -----
  function initButtons() {
    setConsultActv(false); setAjoutActv(false); setModifActv(true);
    setEnregActv(true); setSupActv(true); setImpActv(true);
    setActuActv(true); setAnnulActv(true); setIsEditing(false);
  }

  function handleAjout() {
    form.resetFields();
    setAjoutActv(true); setEnregActv(false); setAnnulActv(false);
    setConsultActv(true); setIsEditing(true); setSbmit('add');
    setGarantiesTableau([]); setDataGarantie([]);
    setFraisOptions([]); setFraisSelectionnes([]); setFraisData([]);
    setIsGarantieOblig(false);
    localStorage.removeItem(Data.LocalVariable.garantie);
    localStorage.removeItem('garanties_client');
    form.setFieldValue('FraisService', 0);
    form.setFieldValue('CodeFrais', []);
  }

  function handleConsult() {
    setConsultActv(true); setAjoutActv(true); setModifActv(false);
    setAnnulActv(false); setSupActv(false); setImpActv(false);
    setIsEditing(false);
  }

  function handleModif() {
    setModifActv(true); setAjoutActv(true); setEnregActv(false);
    setConsultActv(true); setIsEditing(true); setSbmit('update');
  }

  function handleAnnul() {
    initButtons();
    form.resetFields();
    setSbmit('');
    setGarantiesTableau([]); setDataGarantie([]);
    setFraisOptions([]); setFraisSelectionnes([]); setFraisData([]);
    setIsGarantieOblig(false);
    localStorage.removeItem(Data.LocalVariable.garantie);
    localStorage.removeItem('garanties_client');
    form.setFieldValue('FraisService', 0);
    form.setFieldValue('CodeFrais', []);
  }

  // Callback pour le changement de type Salam (appelé depuis Tab1)
  const handleSalamChange = () => {
    const codeSalam = form.getFieldValue('CodeSalam');
    form.setFieldValue('CPTREGLEMENT', undefined);
    loadFraisParams(codeSalam); // Chargement explicite
  };

  // ----- Onglets -----
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: <span><UserOutlined /> Demande Salam</span>,
      children: <Tab1 form={form} disabled={!isEditing} LoadGarantie={LoadGarantie} onSalamChange={handleSalamChange} />,
    },
    {
      key: '2',
      label: <span><ContactsOutlined /> Garanties</span>,
      children: <Tab2 form={form} Datagarantie={dataGarantie} garantiesTableau={garantiesTableau} setGarantiesTableau={setGarantiesTableau} />,
    },
    {
      key: '3',
      label: <span><ContactsOutlined /> Frais de service</span>,
      children: <Tab3 form={form} fraisOptions={fraisOptions} fraisSelectionnes={fraisSelectionnes} setFraisSelectionnes={setFraisSelectionnes} />,
    }
  ];

  // ----- Consultation -----
  function consult() {
    const numero = form.getFieldValue("NumeroDemande");
    if (!numero) {
      SweetAlert.fire({ title: "Numéro requis", text: "Veuillez saisir un numéro.", icon: "warning" });
      return;
    }
    setLoading(true);
    const encryptedNumero = Crypter.encrypt(numero);
    const encryptedAction = Crypter.encrypt("READ_DEMANDE");
    const config = {
      method: 'POST',
      url: Data.ApiUrl.ReadFinancementSalam,
      headers: {
        'Authorization': authHeader(),
        'Content-Type': 'application/json',
        'CypherNumeroDemandeSalam': encryptedNumero,
        'CypherAction': encryptedAction
      }
    };
    axios.request(config)
      .then(response => {
        setLoading(false);
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (res.StatutCode === 200) {
          const data = res.Result.Table1[0];
          const codeFrais = data.CodeFrais || [];

          // Mise à jour du formulaire
          const formValues = {
            ...data,
            DateLivraison: data.DateLivraison ? ReformatDate(data.DateLivraison) : '',
            CodeFrais: codeFrais,
          };
          form.setFieldsValue(formValues);

          // Chargement des paramètres Salam (pour les options)
          loadFraisParams(data.CodeSalam);
          // Forcer la sélection avec ceux de la demande
          setFraisSelectionnes(codeFrais);

          // Garanties
          const garanties = res.Result.Table2 || [];
          if (garanties.length > 0) {
            const mapped = garanties.map((g: any) => ({
              codgar: g.CodeGarantie,
              ...g,
              VALGARANTI: g.VALGARANTI || 0,
              valCouvert: g.MontantCouvert || 0,
            }));
            setGarantiesTableau(mapped);
            localStorage.setItem(Data.LocalVariable.garantie, JSON.stringify(mapped));
          } else {
            setGarantiesTableau([]);
            localStorage.removeItem(Data.LocalVariable.garantie);
          }

          // Prix unitaire (calculé)
          const qte = data.Quantite || 0;
          const mt = data.MontantDemande || 0;
          form.setFieldValue('PrixUnitaire', (qte > 0 && mt > 0) ? Math.round((mt / qte) * 100) / 100 : 0);

          LoadGarantie();
          handleConsult();
        } else {
          SweetAlert.fire({ title: res.Message, text: res.Result || "Erreur", icon: "warning" });
        }
      })
      .catch(error => {
        setLoading(false);
        console.error(error);
        SweetAlert.fire({ title: "Erreur", text: error.toString(), icon: "error" });
      });
  }

  // ----- Soumission -----
  const onFinish = () => {
    const values = form.getFieldsValue(true);
    const garanties = garantiesTableau
      .filter(g => g.codgar && g.codgar.trim() !== '') 
      .map((g) => ({ CodeGarantie: g.codgar.trim(), MontantCouvert: g.valCouvert || 0 }));

    if (isGarantieOblig) {
      SweetAlert.fire({ title: "Garantie obligatoire", text: "Ce type de Salam exige au moins une garantie.", icon: "warning" });
      return;
    }

    const demande: DemandeSalamDto = {
      ...values,
      CODAGE: user.codage || '',
      UtilDemande: user.coduti,
      Garanties: garanties,
      CodeFrais: values.CodeFrais || [],
    };

    if (sbmit === 'update') { demande.NumeroDemande = values.NumeroDemande; }

    const isAdd = sbmit === 'add';
    const url = isAdd ? Data.ApiUrl.CreateDemandSalam : Data.ApiUrl.UpdateDemandSalam;

    SweetAlert.fire({
      title: isAdd
        ? `Voulez-vous enregistrer la demande Salam ${demande.CodeSalam}\n Client : ${demande.NUMCLI}\n Montant : ${formatNumber(values.MontantDemande)} XAF ?`
        : `Voulez-vous modifier la demande ${values.NumeroDemande} ?`,
      showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Non",
      icon: "question", confirmButtonColor: "green", cancelButtonColor: "darkred"
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true); setIsSubmit(true);
        axios({
          method: 'post', url, data: Crypter.encrypt(JSON.stringify(demande)),
          headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' }
        })
        .then((response) => {
          setLoading(false);
          const res = JSON.parse(Crypter.decrypt(response.data)).Value;
          if (res.StatutCode === 200 || res.StatutCode === 201) {
            SweetAlert.fire({ title: res.Message, text: res.Result, icon: "success" });
            handleAnnul();
          } else {
            SweetAlert.fire({ title: res.Message, text: res.Result, icon: "warning" });
          }
        })
        .catch((error) => {
          setLoading(false);
          console.error(error);
          SweetAlert.fire({ title: "Erreur", text: error.toString(), icon: "error" });
        });
      }
    });
  };

  const onFinishFailed = (errorInfo: any) => {
    const errors = errorInfo.errorFields.map((error: any) => error.name.join('.'));
    SweetAlert.fire({ title: "Champs obligatoires", text: "Champs requis : " + errors.join(' - '), icon: 'warning' });
  };

  // ----- Rendu -----
  return (
    <div className="tab">
      <Typography align="center" className="formTitle">
        <b>DEMANDE SALAM</b>
      </Typography>
      <Spin spinning={loading} size="large" style={{ color: 'green' }}>
        <Form preserve={true} disabled={!isEditing} autoComplete="off" labelAlign="left" form={form} name="demande_salam" onFinish={onFinish} onFinishFailed={onFinishFailed}>
          <div className="bG">
            <legend><Typography color="darkgreen">Actions</Typography></legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size="large" className="bt_add" disabled={consultActv} onClick={consult} type="ghost" block>Consulter</Button>
                <Button size="large" className="bt_add" disabled={ajoutActv} onClick={handleAjout} type="ghost" block>Ajouter</Button>
                <Button size="large" className="bt_add" disabled={modifActv} onClick={handleModif} type="ghost" block>Modifier</Button>
                <Button size="large" className="bt_add" disabled={enregActv} type="ghost" htmlType="submit" block>Enregistrer</Button>
                {/* <Button size="large" className="bt_add" disabled={supActv} type="ghost" block>Supprimer</Button>
                <Button size="large" className="bt_add" disabled={impActv} type="ghost" block>Aperçu</Button> */}
                <Button size="large" className="bt_add" disabled={actuActv} type="ghost" block>Actualiser</Button>
                <Button size="large" disabled={annulActv} onClick={handleAnnul} className="bt_add" type="ghost" htmlType="reset" block>Annuler</Button>
              </Space>
            </Form.Item>
            <HelpInfos helpInfo={HelpData.Pret.DemandeDePret as Help} />
          </div>
          <div className="clientP">
            <Tabs size="large" type="card" defaultActiveKey="1" items={items} />
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default DemandeSalam;